import React, { useEffect, useMemo, useState } from 'react';
import { 
  Search, 
  ArrowUpDown, 
  CheckCircle, 
  XCircle, 
  Clock,
  CreditCard,
  Building2,
  User,
  Phone,
  IndianRupee
} from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../slices/types';
import { fetchWithdrawRequests, setActiveStatus, reviewWithdrawRequest } from '../../slices/finance/withdrawRequestsSlice';
import { fetchSupportSettings, updateSupportSettingsPatch } from '../../slices/support/supportSlice';
import api from '../../helpers/api_helper';

interface WithdrawRecord {
  id: number;
  name: string;
  phone: string;
  account_number: string;
  amount: number;
  request_type: string;
  withdraw_mode: 'upi' | 'bank';
  branch_name: string;
  account_type: string;
  bank_name: string;
  bank_status: string;
  bank_ifsc: string;
  status: 'pending' | 'approved' | 'completed' | 'rejected' | 'success' | 'failed';
  gateway: string;
  created_at: string;
}

const getTodayBusinessDate = (): string => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parts.find(part => part.type === 'year')?.value || '';
  const month = parts.find(part => part.type === 'month')?.value || '';
  const day = parts.find(part => part.type === 'day')?.value || '';

  return `${year}-${month}-${day}`;
};

const WithdrawRequest: React.FC = () => {
  const dispatch = useAppDispatch();
  const { items, loading, error, activeStatus } = useAppSelector(s => s.WithdrawRequests);
  const { settings: supportSettings } = useAppSelector(s => s.Support);

  const [withdrawalToggleLoading, setWithdrawalToggleLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionInfo, setActionInfo] = useState<{
    type: 'success' | 'info' | 'error';
    title: string;
    reason?: string;
    notes?: string;
  } | null>(null);
  const [rejectTarget, setRejectTarget] = useState<{
    id: number;
    reason: string;
    notes: string;
    customReason: string;
  } | null>(null);

  const [returnWalletTarget, setReturnWalletTarget] = useState<{
    id: number;
    name: string;
    phone: string;
    amount: number;
    reason: string;
  } | null>(null);

  const [returnWalletLoading, setReturnWalletLoading] =
    useState(false);
  const [sortField, setSortField] = useState<keyof WithdrawRecord>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [exportingStatus, setExportingStatus] = useState<string | null>(null);
  const [filterData, setFilterData] = useState(() => {
    const today = getTodayBusinessDate();

    return {
      name: '',
      account_number: '',
      bank_name: '',
      bank_ifsc: '',
      bet_status: 'all',
      mode: 'all',
      date_from: today,
      date_to: today,
    };
  });

  const [appliedRequestFilters, setAppliedRequestFilters] = useState(() => {
    const today = getTodayBusinessDate();

    return {
      status: undefined as 'pending' | 'approved' | 'completed' | undefined,
      mode: undefined as 'upi' | 'imps_neft_rtgs' | 'qr' | undefined,
      date_from: today,
      date_to: today,
    };
  });

  useEffect(() => {
    const appId =
      localStorage.getItem('app_id') ||
      localStorage.getItem('selectedAppId') ||
      '4bad143c-4ed5-4681-adb1-e6f5b751dd97';

    if (appId) {
      dispatch(fetchSupportSettings(appId));
    }
  }, [dispatch]);

  useEffect(() => {
    const fetchWithdrawals = () => {
      const statusParam =
        activeStatus === 'all'
          ? undefined
          : activeStatus;

      dispatch(
        fetchWithdrawRequests({
          status: appliedRequestFilters.status ?? statusParam,
          mode: appliedRequestFilters.mode,
          date_from: appliedRequestFilters.date_from || undefined,
          date_to: appliedRequestFilters.date_to || undefined,
        })
      );
    };

    // Load immediately when the page/status changes.
    fetchWithdrawals();

    // Keep the withdrawal list fresh without reloading the page.
    const refreshInterval = window.setInterval(
      fetchWithdrawals,
      5000
    );

    return () => {
      window.clearInterval(refreshInterval);
    };
  }, [
    dispatch,
    activeStatus,
    appliedRequestFilters.status,
    appliedRequestFilters.mode,
    appliedRequestFilters.date_from,
    appliedRequestFilters.date_to
  ]);

  const handleExportExcel = async (
    statusValue: 'pending' | 'completed' | 'all'
  ) => {
    try {
      setExportingStatus(statusValue);

      const response = await api.get(
        '/admin/withdrawal-requests/export/',
        {
          params: {
            status: statusValue,
          },
          responseType: 'blob',
        }
      );

      const contentDisposition =
        response.headers?.['content-disposition'] || '';

      const filenameMatch =
        contentDisposition.match(/filename="?([^";]+)"?/i);

      const filename =
        filenameMatch?.[1] ||
        `Pari567_Withdrawals_${statusValue}.xlsx`;

      const blob = new Blob(
        [response.data],
        {
          type:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }
      );

      const downloadUrl =
        window.URL.createObjectURL(blob);

      const anchor =
        document.createElement('a');

      anchor.href = downloadUrl;
      anchor.download = filename;

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(downloadUrl);

      setActionInfo({
        type: 'success',
        title: 'Excel exported successfully',
        notes: `${filename} downloaded successfully`,
      });
    } catch (err: any) {
      setActionInfo({
        type: 'error',
        title: 'Failed to export Excel',
        notes:
          err?.message ||
          'Unable to export withdrawal Excel file',
      });
    } finally {
      setExportingStatus(null);
    }
  };

  // Transform API items into UI model
  const withdrawRecords: WithdrawRecord[] = useMemo(() => (items || []).map((it) => ({
    id: it.id,
    // Name column shows Account Holder Name
    name: it.bank_account?.account_holder_name ?? '-',
    // Player mobile number supplied by the withdrawal API.
    phone: it.user_mobile ?? it.user?.mobile_number ?? '-',
    // Bank account number remains a separate field.
    account_number: it.bank_account?.account_number ?? '-',
    amount: Number(it.amount ?? 0),
    request_type: 'debit',
    withdraw_mode: 'bank',
    // new columns
    branch_name: it.bank_account?.branch_name ?? '-',
    account_type: it.bank_account?.account_type ?? '-',
    bank_name: it.bank_account?.bank_name ?? '-',
    bank_status: it.bank_account?.status ?? '-',
    // map Bank IFSC from response
    bank_ifsc: it.bank_account?.ifsc_code ?? '-',
    status: (it.status as any),
    gateway: String(it.gateway ?? ''),
    created_at: it.created_at,
  })), [items]);

  // Filter and sort data
  const filteredRecords = withdrawRecords
    .filter(record => {
      const matchesSearch =
        record.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.phone.includes(searchTerm) ||
        record.account_number.includes(searchTerm) ||
        record.id.toString().includes(searchTerm);
      const matchesName = !filterData.name || record.name.toLowerCase().includes(filterData.name.toLowerCase());
      const matchesAcc = !filterData.account_number || record.account_number.includes(filterData.account_number);
      const matchesBank = !filterData.bank_name || record.bank_name.toLowerCase().includes(filterData.bank_name.toLowerCase());
      const matchesIfsc = !filterData.bank_ifsc || record.bank_ifsc.toLowerCase().includes(filterData.bank_ifsc.toLowerCase());
      
      // Date filtering is handled server-side after Apply Filters.
      const matchesDateFrom = true;
      const matchesDateTo = true;

      // Status filtering
      const matchesStatus = filterData.bet_status === 'all' || 
        (filterData.bet_status === 'pending' && record.status === 'pending') ||
        (filterData.bet_status === 'approved' && record.status === 'approved') ||
        (filterData.bet_status === 'completed' && record.status === 'completed');
      
      return matchesSearch && matchesName && matchesAcc && matchesBank && matchesIfsc && 
             matchesDateFrom && matchesDateTo && matchesStatus;
    })
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (field: keyof WithdrawRecord) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'pending':
        return 'text-blue-600 bg-orange-50';
      case 'rejected':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'completed':
      case 'success':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const withdrawalsEnabled =
    supportSettings?.withdrawals_enabled !== false;

  const handleWithdrawalToggle = async () => {
    const nextEnabled = !withdrawalsEnabled;

    const confirmed = window.confirm(
      nextEnabled
        ? 'Enable new withdrawal requests?'
        : 'Disable new withdrawal requests? Existing requests will still be processable.'
    );

    if (!confirmed) {
      return;
    }

    const appId =
      localStorage.getItem('app_id') ||
      localStorage.getItem('selectedAppId') ||
      supportSettings?.app_id ||
      '4bad143c-4ed5-4681-adb1-e6f5b751dd97';

    if (!appId) {
      setActionInfo({
        type: 'error',
        title: 'Unable to determine the current app.'
      });
      return;
    }

    try {
      setWithdrawalToggleLoading(true);

      await dispatch(
        updateSupportSettingsPatch({
          appId,
          payload: {
            withdrawals_enabled: nextEnabled
          }
        })
      ).unwrap();

      setActionInfo({
        type: 'success',
        title: nextEnabled
          ? 'Withdrawals enabled successfully.'
          : 'Withdrawals disabled successfully.'
      });
    } catch (toggleError: any) {
      setActionInfo({
        type: 'error',
        title:
          typeof toggleError === 'string'
            ? toggleError
            : 'Failed to update withdrawal status.'
      });
    } finally {
      setWithdrawalToggleLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">Withdraw Request</h1>

            <button
              type="button"
              disabled={withdrawalToggleLoading}
              onClick={handleWithdrawalToggle}
              className={`px-4 py-2 rounded-lg text-xs font-semibold text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                withdrawalsEnabled
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              {withdrawalToggleLoading
                ? 'Updating...'
                : withdrawalsEnabled
                  ? 'Disable Withdrawals'
                  : 'Enable Withdrawals'}
            </button>

            <span
              className={`px-3 py-1 rounded-full text-xs font-semibold ${
                withdrawalsEnabled
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {withdrawalsEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <div className="flex items-center space-x-2">
            <button
              className={`px-3 py-1 rounded text-xs ${activeStatus==='all'?'bg-blue-600 text-white':'bg-gray-100 text-gray-700'}`}
              onClick={() => dispatch(setActiveStatus('all'))}
            >All</button>
            <button
              className={`px-3 py-1 rounded text-xs ${activeStatus==='pending'?'bg-blue-600 text-white':'bg-gray-100 text-gray-700'}`}
              onClick={() => dispatch(setActiveStatus('pending'))}
            >Pending</button>
            <button
              className={`px-3 py-1 rounded text-xs ${activeStatus==='approved'?'bg-blue-600 text-white':'bg-gray-100 text-gray-700'}`}
              onClick={() => dispatch(setActiveStatus('approved'))}
            >Approved</button>
            
          </div>
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
              />
            </div>
            <button
              type="button"
              disabled={exportingStatus !== null}
              onClick={() => handleExportExcel('pending')}
              className="px-3 py-2.5 rounded text-xs bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingStatus === 'pending'
                ? 'Exporting...'
                : 'Export Pending'}
            </button>

            <button
              type="button"
              disabled={exportingStatus !== null}
              onClick={() => handleExportExcel('completed')}
              className="px-3 py-2.5 rounded text-xs bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingStatus === 'completed'
                ? 'Exporting...'
                : 'Export Completed'}
            </button>

            <button
              type="button"
              disabled={exportingStatus !== null}
              onClick={() => handleExportExcel('all')}
              className="px-3 py-2.5 rounded text-xs bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingStatus === 'all'
                ? 'Exporting...'
                : 'Export All'}
            </button>

            <button
              onClick={()=>setShowFilters(!showFilters)}
              className="px-5 py-2.5 rounded text-xs bg-gray-600 text-white"
            >Filters</button>
          </div>
        </div>
      </div>

      {/* Action Feedback Card */}
      {actionInfo && (
        <div className={`mb-6 rounded-lg border ${
          actionInfo.type === 'success' ? 'bg-green-50 border-green-200' :
          actionInfo.type === 'error' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-blue-200'
        }`}>
          <div className="p-4 flex items-start justify-between">
            <div>
              <h3 className={`font-semibold mb-1 ${
                actionInfo.type === 'success' ? 'text-green-800' :
                actionInfo.type === 'error' ? 'text-red-800' : 'text-blue-800'
              }`}>{actionInfo.title}</h3>
              {(actionInfo.reason || actionInfo.notes) && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {actionInfo.reason && (
                    <div className="rounded-md bg-white shadow-sm border p-3">
                      <div className="text-xs uppercase text-gray-500 mb-1">Reason</div>
                      <div className="text-sm text-gray-800">{actionInfo.reason}</div>
                    </div>
                  )}
                  {actionInfo.notes && (
                    <div className="rounded-md bg-white shadow-sm border p-3">
                      <div className="text-xs uppercase text-gray-500 mb-1">Admin notes</div>
                      <div className="text-sm text-gray-800">{actionInfo.notes}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              className="ml-4 text-sm text-gray-500 hover:text-gray-700"
              onClick={() => setActionInfo(null)}
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Return to Wallet Modal */}
      {returnWalletTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Return Withdrawal to Wallet
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                This will credit the exact withdrawal amount back to the player's available wallet.
              </p>
            </div>

            <div className="space-y-4 px-6 py-5">
              <div className="rounded-lg border bg-gray-50 p-4">
                <div className="flex justify-between gap-4">
                  <span className="text-sm text-gray-500">Player</span>
                  <span className="text-sm font-medium text-gray-900">
                    {returnWalletTarget.name}
                  </span>
                </div>

                <div className="mt-2 flex justify-between gap-4">
                  <span className="text-sm text-gray-500">Mobile</span>
                  <span className="text-sm font-medium text-gray-900">
                    {returnWalletTarget.phone}
                  </span>
                </div>

                <div className="mt-2 flex justify-between gap-4">
                  <span className="text-sm text-gray-500">
                    Amount to Return
                  </span>
                  <span className="text-base font-bold text-emerald-700">
                    ₹{returnWalletTarget.amount.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Reason / Remark
                </label>

                <textarea
                  value={returnWalletTarget.reason}
                  onChange={(e) =>
                    setReturnWalletTarget((prev) =>
                      prev
                        ? {
                            ...prev,
                            reason: e.target.value,
                          }
                        : prev
                    )
                  }
                  rows={4}
                  maxLength={250}
                  placeholder="Example: Invalid bank account number"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                />

                <p className="mt-1 text-xs text-gray-500">
                  Required. This reason will be stored in the wallet journal and withdrawal audit details.
                </p>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Confirm only if the withdrawal was not successfully paid externally.
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                type="button"
                disabled={returnWalletLoading}
                onClick={() => setReturnWalletTarget(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={
                  returnWalletLoading ||
                  !returnWalletTarget.reason.trim()
                }
                onClick={async () => {
                  if (!returnWalletTarget) return;

                  const reason =
                    returnWalletTarget.reason.trim();

                  if (!reason) {
                    setActionInfo({
                      type: 'error',
                      title: 'Reason required',
                      notes: 'Please enter a reason before returning the amount.',
                    });
                    return;
                  }

                  const confirmed = window.confirm(
                    `Return ₹${returnWalletTarget.amount.toLocaleString('en-IN')} to ${returnWalletTarget.name}'s wallet?`
                  );

                  if (!confirmed) return;

                  try {
                    setReturnWalletLoading(true);

                    const response = await api.post(
                      `/admin/withdrawal-requests/${returnWalletTarget.id}/return-to-wallet/`,
                      { reason }
                    );

                    setActionInfo({
                      type: 'success',
                      title:
                        response.data?.message ||
                        'Withdrawal returned to wallet',
                      notes: `₹${returnWalletTarget.amount.toLocaleString('en-IN')} returned successfully.`,
                    });

                    setReturnWalletTarget(null);

                    const statusParam =
                      activeStatus === 'all'
                        ? undefined
                        : activeStatus;

                    dispatch(
                      fetchWithdrawRequests({
                        status: statusParam,
                        page: 1,
                        page_size: 25,
                      })
                    );
                  } catch (err: any) {
                    setActionInfo({
                      type: 'error',
                      title: 'Failed to return withdrawal',
                      notes:
                        err?.response?.data?.error ||
                        err?.response?.data?.detail ||
                        err?.message ||
                        'Unable to return withdrawal amount.',
                    });
                  } finally {
                    setReturnWalletLoading(false);
                  }
                }}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {returnWalletLoading
                  ? 'Returning...'
                  : 'Return to Wallet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Form Card */}
      {rejectTarget && (
        <div className="mb-6 rounded-lg border bg-amber-50 border-amber-200">
          <div className="p-4">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-semibold text-amber-900">Reject withdrawal request</h3>
              <button
                className="text-sm text-amber-600 hover:text-amber-800"
                onClick={() => setRejectTarget(null)}
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase text-amber-800 mb-1">Reason</label>
                <select
                  className="w-full px-3 py-2 rounded border border-amber-300 bg-white"
                  value={rejectTarget.reason}
                  onChange={(e) =>
                    setRejectTarget((prev) =>
                      prev
                        ? {
                            ...prev,
                            reason: e.target.value,
                            customReason:
                              e.target.value === 'Other'
                                ? prev.customReason
                                : '',
                          }
                        : prev
                    )
                  }
                >
                  <option value="">Select rejection reason</option>
                  <option value="Insufficient wallet balance">
                    Insufficient wallet balance
                  </option>
                  <option value="Invalid bank details">
                    Invalid bank details
                  </option>
                  <option value="Bank account not verified">
                    Bank account not verified
                  </option>
                  <option value="Duplicate withdrawal request">
                    Duplicate withdrawal request
                  </option>
                  <option value="Suspicious or invalid request">
                    Suspicious or invalid request
                  </option>
                  <option value="Other">Other</option>
                </select>

                {rejectTarget.reason === 'Other' && (
                  <input
                    type="text"
                    placeholder="Enter rejection reason"
                    className="w-full mt-2 px-3 py-2 rounded border border-amber-300 bg-white"
                    value={rejectTarget.customReason}
                    onChange={(e) =>
                      setRejectTarget((prev) =>
                        prev
                          ? { ...prev, customReason: e.target.value }
                          : prev
                      )
                    }
                  />
                )}
              </div>
              <div>
                <label className="block text-xs uppercase text-amber-800 mb-1">Admin notes</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 rounded border border-amber-300 bg-white"
                  value={rejectTarget.notes}
                  onChange={(e)=>setRejectTarget(prev=> prev?({ ...prev, notes: e.target.value }):prev)}
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                className="px-4 py-2 rounded bg-amber-600 text-white hover:bg-amber-700 text-sm"
                onClick={() => {
                  if (!rejectTarget) return;

                  const finalReason =
                    rejectTarget.reason === 'Other'
                      ? rejectTarget.customReason.trim()
                      : rejectTarget.reason.trim();

                  if (!finalReason) {
                    setActionInfo({
                      type: 'error',
                      title: 'Rejection reason required',
                      notes: 'Please select or enter a rejection reason.',
                    });
                    return;
                  }

                  dispatch(
                    reviewWithdrawRequest({
                      id: rejectTarget.id,
                      status: 'rejected',
                      rejection_reason: finalReason,
                      admin_notes: rejectTarget.notes,
                    })
                  )
                    .unwrap()
                    .then((res) => {
                      setActionInfo({
                        type: 'info',
                        title: res.message,
                        reason: finalReason,
                        notes: rejectTarget.notes,
                      });
                      setRejectTarget(null);
                      const statusParam =
                        activeStatus === 'all'
                          ? undefined
                          : activeStatus;
                      dispatch(
                        fetchWithdrawRequests({
                          status: statusParam,
                        })
                      );
                    })
                    .catch((err) =>
                      setActionInfo({
                        type: 'error',
                        title: 'Failed to reject',
                        notes: String(err),
                      })
                    );
                }}
              >
                Submit Rejection
              </button>
              <button
                className="px-4 py-2 rounded bg-gray-200 text-gray-700 text-sm"
                onClick={()=> setRejectTarget(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Filters */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                placeholder="Account holder name"
                value={filterData.name}
                onChange={(e)=>setFilterData(prev=>({...prev, name: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
              <input
                type="text"
                placeholder="Account number"
                value={filterData.account_number}
                onChange={(e)=>setFilterData(prev=>({...prev, account_number: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
              <input
                type="text"
                placeholder="Bank name"
                value={filterData.bank_name}
                onChange={(e)=>setFilterData(prev=>({...prev, bank_name: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bank IFSC</label>
              <input
                type="text"
                placeholder="IFSC code"
                value={filterData.bank_ifsc}
                onChange={(e)=>setFilterData(prev=>({...prev, bank_ifsc: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filterData.bet_status}
                onChange={(e)=>setFilterData(prev=>({...prev, bet_status: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
              <select
                value={filterData.mode}
                onChange={(e)=>setFilterData(prev=>({...prev, mode: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Modes</option>
                <option value="upi">UPI</option>
                <option value="imps_neft_rtgs">IMPS/NEFT/RTGS</option>
                <option value="qr">QR</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date From</label>
              <input
                type="date"
                placeholder="dd-mm-yyyy"
                value={filterData.date_from}
                onChange={(e)=>setFilterData(prev=>({...prev, date_from: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date To</label>
              <input
                type="date"
                placeholder="dd-mm-yyyy"
                value={filterData.date_to}
                onChange={(e)=>setFilterData(prev=>({...prev, date_to: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-3">
            <button
              onClick={()=>{
                const statusParam = filterData.bet_status === 'all'
                  ? undefined
                  : (filterData.bet_status as 'pending' | 'approved' | 'completed');

                const modeParam = filterData.mode === 'all'
                  ? undefined
                  : (filterData.mode as 'upi' | 'imps_neft_rtgs' | 'qr');

                setAppliedRequestFilters({
                  status: statusParam,
                  mode: modeParam,
                  date_from: filterData.date_from,
                  date_to: filterData.date_to,
                });

                dispatch(fetchWithdrawRequests({
                  status: statusParam,
                  mode: modeParam,
                  date_from: filterData.date_from || undefined,
                  date_to: filterData.date_to || undefined
                }));
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg"
            >Apply Filters</button>
            <button
              onClick={()=>{
                const today = getTodayBusinessDate();

                setFilterData({
                  name:'',
                  account_number:'',
                  bank_name:'',
                  bank_ifsc:'',
                  bet_status: 'all',
                  mode: 'all',
                  date_from: today,
                  date_to: today
                });

                setAppliedRequestFilters({
                  status: undefined,
                  mode: undefined,
                  date_from: today,
                  date_to: today,
                });
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg"
            >Clear All</button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('id')}
                >
                  <div className="flex items-center space-x-1">
                    <span>ID</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Name</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('phone')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Phone Number</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('account_number')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Account Number</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Amount</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('request_type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Request Type</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('withdraw_mode')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Withdraw Mode</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('branch_name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Branch Name</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('account_type')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Account Type</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bank Name
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('bank_status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Bank Status</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bank IFSC
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Created At</span>
                    <ArrowUpDown className="w-3 h-3" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRecords.map((record, index) => (
                <tr key={record.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {record.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      {record.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      {record.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.account_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      <IndianRupee className="w-4 h-4 text-gray-400 mr-2" />
                      {formatAmount(record.amount)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.request_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center">
                      {record.withdraw_mode === 'upi' ? (
                        <CreditCard className="w-4 h-4 text-blue-500 mr-2" />
                      ) : (
                        <Building2 className="w-4 h-4 text-green-500 mr-2" />
                      )}
                      {record.withdraw_mode.toUpperCase()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.branch_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                    {record.account_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.bank_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${record.bank_status==='verified' ? 'text-green-700 bg-green-50' : 'text-gray-700 bg-gray-50'}`}>
                      {record.bank_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {record.bank_ifsc}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}>
                      {getStatusIcon(record.status)}
                      <span className="ml-1">{record.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {record.status === 'pending' ? (
                      <div className="flex space-x-2">
                        <button
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                          onClick={() => {
                            dispatch(reviewWithdrawRequest({
                              id: record.id,
                              status: 'approved',
                              admin_notes: 'Approved after verification'
                            }))
                              .unwrap()
                              .then((res) => {
                                setActionInfo({
                                  type: 'success',
                                  title: res.message,
                                  notes: (res.withdrawal_request as any)?.admin_notes || 'Approved after verification'
                                });
                                // refresh list
                                const statusParam = activeStatus === 'all' ? undefined : activeStatus;
                                dispatch(fetchWithdrawRequests({ status: statusParam, page: 1, page_size: 25 }));
                              })
                              .catch((err) => setActionInfo({ type: 'error', title: 'Failed to approve', notes: String(err) }));
                          }}
                        >
                          Approve
                        </button>
                        <button
                          className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-3 py-1 rounded text-xs"
                          onClick={() =>
                            setRejectTarget({
                              id: record.id,
                              reason: '',
                              notes: '',
                              customReason: '',
                            })
                          }
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      record.status === 'completed' &&
                      record.gateway === 'manual' ? (
                        <button
                          type="button"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded text-xs"
                          onClick={() =>
                            setReturnWalletTarget({
                              id: record.id,
                              name: record.name,
                              phone: record.phone,
                              amount: record.amount,
                              reason: '',
                            })
                          }
                        >
                          Return to Wallet
                        </button>
                      ) : (
                        <span className="text-gray-500">
                          No Action
                        </span>
                      )
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(record.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WithdrawRequest;
